import { useEffect, useMemo, useState } from 'react';
import {
  approveDelegation, createDelegation, listDelegations, listEmployees, revokeDelegation,
  type DelegationRow, type EmployeeRecord,
} from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { Icon } from '../../components/Icon';

const AUTHORITY_TYPES = [
  'expense', 'reimbursement', 'vendor_payment', 'purchase', 'travel_booking',
  'vendor_engagement', 'quotation', 'leave', 'wfh_request', 'production_deployment',
];

export function Delegations() {
  const { can, session } = useAuth();
  const [rows, setRows] = useState<DelegationRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    delegateEmployeeId: '', authorityType: 'expense', scope: '',
    monetaryLimit: '', startsOn: '', endsOn: '', reason: '',
  });

  const load = () => Promise.all([listDelegations(), listEmployees({})])
    .then(([d, e]) => { setRows(d.items); setEmployees(e.items); setError(null); })
    .catch((err: Error) => setError(err.message));

  useEffect(() => { load(); }, []);

  const nameOf = useMemo(() => {
    const map = new Map(employees.map((e) => [e.id, `${e.first_name} ${e.last_name}`.trim()]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [employees]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      await createDelegation({
        delegateEmployeeId: form.delegateEmployeeId,
        authorityType: form.authorityType,
        scope: form.scope || null,
        monetaryLimit: form.monetaryLimit ? Number(form.monetaryLimit) : null,
        startsOn: form.startsOn, endsOn: form.endsOn, reason: form.reason,
      });
      setNotice('Delegation created. It grants nothing until it is approved by someone other than the delegator or the delegate.');
      setForm({ ...form, scope: '', monetaryLimit: '', reason: '' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The delegation was refused.');
    } finally { setBusy(false); }
  }

  async function act(fn: () => Promise<unknown>, done: string) {
    setBusy(true); setError(null); setNotice(null);
    try { await fn(); setNotice(done); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : 'That action was refused.'); }
    finally { setBusy(false); }
  }

  const others = employees.filter((e) => e.id !== session?.user.employeeId && e.status !== 'exited');

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="handshake" size="xs" /><span>Approvals</span></span>
          <h1>Delegation of Authority</h1>
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}
      {notice && <div className="banner info" style={{ marginBottom: 14 }}>{notice}</div>}

      <div className="org-grid">
        <div className="panel">
          <div className="panel-head">
            <strong>{rows.length} delegation{rows.length === 1 ? '' : 's'}</strong>
          </div>
          {rows.length === 0 ? (
            <div className="empty">
              <strong>No authority is currently delegated</strong>
              <p>Every approval routes to its configured authority.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>From → To</th>
                    <th>Authority</th>
                    <th style={{ width: 120 }}>Limit</th>
                    <th style={{ width: 170 }}>Window</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 150 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <strong>{nameOf(d.delegator_employee_id)}</strong>
                        <div className="muted" style={{ fontSize: 12 }}>→ {nameOf(d.delegate_employee_id)}</div>
                      </td>
                      <td>
                        {d.authority_type.replace(/_/g, ' ')}
                        {d.scope && <div className="muted" style={{ fontSize: 12 }}>{d.scope}</div>}
                      </td>
                      <td className="mono">{d.monetary_limit != null ? `${d.currency} ${d.monetary_limit.toLocaleString('en-IN')}` : '—'}</td>
                      <td className="mono">{d.starts_on.slice(0, 10)} → {d.ends_on.slice(0, 10)}</td>
                      <td><span className={`org-chip org-chip-${d.status}`}>{d.status}</span></td>
                      <td>
                        {d.status === 'pending' && can('delegation.approve') && (
                          <button type="button" disabled={busy} onClick={() => act(() => approveDelegation(d.id), 'Delegation activated.')}>Approve</button>
                        )}
                        {d.status === 'active' && can('delegation.manage') && (
                          <button type="button" disabled={busy} onClick={() => act(() => revokeDelegation(d.id, 'Revoked from the delegation screen'), 'Delegation revoked.')}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted org-footnote">
            A delegation never transfers unlimited authority. Self-delegation, an end date not after the start, and
            blanket authority without a monetary limit are all refused by the server.
          </p>
        </div>

        <div className="org-side">
          {can('delegation.manage') && (
            <form className="panel" onSubmit={submit}>
              <div className="panel-head"><strong>Delegate authority</strong></div>
              <div className="org-form org-form-stack">
                <label>
                  <span>Delegate to</span>
                  <select required value={form.delegateEmployeeId} onChange={(e) => setForm({ ...form, delegateEmployeeId: e.target.value })}>
                    <option value="">Choose a person…</option>
                    {others.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                  </select>
                </label>
                <label>
                  <span>Authority type</span>
                  <select value={form.authorityType} onChange={(e) => setForm({ ...form, authorityType: e.target.value })}>
                    {AUTHORITY_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </label>
                <label><span>Scope</span><input value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="e.g. Travel expenses only" /></label>
                <label><span>Monetary limit (INR)</span><input type="number" min={0} value={form.monetaryLimit} onChange={(e) => setForm({ ...form, monetaryLimit: e.target.value })} /></label>
                <label><span>Starts</span><input type="date" required value={form.startsOn} onChange={(e) => setForm({ ...form, startsOn: e.target.value })} /></label>
                <label><span>Ends</span><input type="date" required value={form.endsOn} onChange={(e) => setForm({ ...form, endsOn: e.target.value })} /></label>
                <label><span>Reason</span><input required minLength={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
              </div>
              <div className="org-form-actions">
                <button className="btn-primary" type="submit" disabled={busy}>Create delegation</button>
              </div>
            </form>
          )}

          <div className="panel">
            <div className="panel-head"><strong>Financial control</strong></div>
            <ol className="org-workflow org-workflow-static">
              <li className="done"><span className="org-workflow-dot" /><span><strong>Prepare &amp; record</strong><em className="muted"> — Accountant</em></span></li>
              <li className="done"><span className="org-workflow-dot" /><span><strong>Approve &amp; release</strong><em className="muted"> — CEO, who holds banking authority</em></span></li>
              <li className="done"><span className="org-workflow-dot" /><span><strong>Reconcile &amp; review</strong><em className="muted"> — independent CA</em></span></li>
            </ol>
            <p className="muted org-footnote">
              Preparing a payment is not authority to release it, and banking credentials are never handled through
              HR screens.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
